// Select

export function loadSelect(value: any): string | number | null {
  if (value === "" || value === null || value === undefined) return null;

  if (!isNaN(value) && value !== "") {
    const num = Number(value);
    if (!isNaN(num)) return num;
  }

  return String(value);
}

export function saveSelect(value: any): string | number | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (!isNaN(value)) {
    const num = Number(value);
    if (!isNaN(num)) return num;
  }

  return String(value);
}

// Tristate

export function loadTristate(value: any): "yes" | "no" | "maybe" | null {
  if (!value || typeof value !== "string") return null;

  const normalized = value.toLowerCase();
  return ["yes", "no", "maybe"].includes(normalized) ? (normalized as any) : null;
}

export function saveTristate(value: any): string {
  return value === null || value === undefined ? "" : String(value);
}

// Date

export function saveDate(date: Date | string | null): string | null {
  if (!date) return null;
  const localDate = new Date(date);
  return new Date(Date.UTC(localDate.getFullYear(), localDate.getMonth(), localDate.getDate(), 0, 0, 0)).toISOString().replace(".000Z", "Z");
}

export function normalizeDate(date: Date | string | null): string {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return "";

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export function loadDate(dateString: string | null): Date | null {
  if (!dateString) return null;
  const [datePart = ""] = dateString.split("T");
  const [year, month, day] = datePart.split("-");
  return new Date(Number(year), Number(month) - 1, Number(day));
}

export function loadDateTime(dateTimeString: string | null): Date | null {
  if (!dateTimeString) return null;
  const parsed = new Date(dateTimeString);
  return isNaN(parsed.getTime()) ? null : parsed;
}

// Array

export function saveArray(array: any[] | null): string {
  if (!array || !Array.isArray(array) || array.length === 0) return "";
  return JSON.stringify(array);
}

export function loadArray(input: string | null): any[] {
  if (!input || typeof input !== "string" || input.trim() === "") return [];
  try {
    return JSON.parse(input);
  } catch (error) {
    return [];
  }
}

// Decimal

export function saveDecimal(decimalVal: number | null): string {
  if (decimalVal == null) {
    return "";
  }
  return decimalVal.toFixed(2);
}

// saveFloat returns a numeric value (or null) for endpoints that expect float64 (not string-encoded).
// Accepts numbers and numeric strings (incl. comma decimal separator, e.g. "1.234,56"). Empty
// string / non-numeric input becomes null so the backend never receives `""` for *float64 fields,
// which would otherwise produce a 400 "Invalid request body" on json.Unmarshal.
export function saveFloat(decimalVal: number | string | null | undefined): number | null {
  if (decimalVal == null) return null;
  if (typeof decimalVal === "number") return Number.isFinite(decimalVal) ? decimalVal : null;
  const trimmed = String(decimalVal).trim();
  if (trimmed === "") return null;
  // Tolerate German "1.234,56" format.
  const normalized = trimmed.includes(",") ? trimmed.replace(/\./g, "").replace(",", ".") : trimmed;
  const num = parseFloat(normalized);
  return Number.isFinite(num) ? num : null;
}

export function loadDecimal(decimalInput: string | number | null): number | null {
  if (decimalInput == null) return null;
  if (typeof decimalInput === "number") return isNaN(decimalInput) ? null : decimalInput;
  if (typeof decimalInput !== "string" || decimalInput.trim() === "") return null;
  const num = parseFloat(decimalInput);
  if (isNaN(num)) {
    return null;
  }
  return num;
}

// Integer

// Accepts numbers and numeric strings; empty / non-numeric input becomes null so the backend
// never receives `""` for *int64 fields (json.Unmarshal would reject with 400).
export function saveInteger(value: number | string | null | undefined): number | null {
  if (value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? Math.trunc(value) : null;
  const trimmed = String(value).trim();
  if (trimmed === "") return null;
  const num = parseInt(trimmed, 10);
  return Number.isFinite(num) ? num : null;
}

export function loadInteger(value: number | string | null): number | null {
  if (value == null) return null;
  if (typeof value === "number") return isNaN(value) ? null : Math.floor(value);
  if (typeof value === "string") {
    if (value.trim() === "") return null;
    const num = parseInt(value, 10);
    return isNaN(num) ? null : num;
  }
  return null;
}

// Boolean

export function loadBoolean(input: any): boolean {
  return input === true || input === "true" || input === 1 || input === "1";
}

// Display

export function displayDate(date: Date): string {
  if (!(date instanceof Date) || isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

export function displayDateTime(date: Date | null): string {
  if (!(date instanceof Date) || isNaN(date.getTime())) return "";

  const datePart = new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);

  const pad = (n: number) => n.toString().padStart(2, "0");
  const timePart = `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;

  return `${datePart} ${timePart}`;
}

export function displayTristate(value: string): string {
  switch (value) {
    case "yes":
      return "Ja";
    case "no":
      return "Nein";
    case "maybe":
      return "Unklar";
    default:
      return "";
  }
}

export function displayCurrency(value: number): string {
  if (typeof value !== "number" || isNaN(value)) return "";

  return value.toLocaleString("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function displayDecimal(value: number): string {
  if (typeof value !== "number" || isNaN(value)) return "";

  return value.toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// Image

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Load data

export function loadUserData(data: any) {
  return {
    ...data,
    lastLoginAt: loadDateTime(data.lastLoginAt),
    createdAt: loadDate(data.createdAt),
    emailVerified: loadBoolean(data.emailVerified),
    deleteRequestedAt: loadDateTime(data.deleteRequestedAt),
  };
}
